<script lang="ts">
	import { screenWidth } from '$lib/stores/resizeStore';
	import toCloudinaryUrl from '$lib/utils/toCloudinarUrl';
	import { afterUpdate } from 'svelte';
	import type { Writable } from 'svelte/store';
	import type { Hub } from '$lib/api/rpc';
	import {
		constructCloudinaryTransformationParam,
		type CoverImageContainType,
		type CoverImageTransformationType,
		type CloudinaryTransformationParamType,
	} from '$lib/components/cover/constructCloudinaryParam';
	import BreakPoint from '$lib/components/BreakPoint/BreakPoint.svelte';
	import type { Device } from '$lib/types/Device';

	export let forDesignPage = false;
	export let hub: Writable<Hub>;
	export let device: Device = 'desktop';

	let cloudinaryTransformationParam: CloudinaryTransformationParamType;
	$: if (device == 'desktop') {
		cloudinaryTransformationParam = constructCloudinaryTransformationParam(
			$hub.theme.desktop.coverImageTransformationType as CoverImageTransformationType,
			$hub.theme.desktop.coverImageContainType as CoverImageContainType
		);
	} else if (device == 'mobile') {
		cloudinaryTransformationParam = constructCloudinaryTransformationParam(
			$hub.theme.mobile.coverImageTransformationType as CoverImageTransformationType,
			$hub.theme.mobile.coverImageContainType as CoverImageContainType
		);
	}

	let y: number;
	let backgroundImgUrl: string | null = null;

	$: {
		if (
			(device == 'desktop' && $hub.theme.desktop.coverImage) ||
			(device == 'mobile' && $hub.theme.mobile.coverImage)
		) {
			if (cloudinaryTransformationParam == 'noCloudinaryTransformation') {
				backgroundImgUrl =
					device == 'desktop' ? $hub.theme.desktop.coverImage : $hub.theme.mobile.coverImage;
			} else {
				backgroundImgUrl = toCloudinaryUrl(
					device == 'desktop' ? $hub.theme.desktop.coverImage : $hub.theme.mobile.coverImage,
					device == 'mobile' && forDesignPage ? 400 : $screenWidth,
					480,
					undefined,
					cloudinaryTransformationParam
				);
			}
		} else {
			backgroundImgUrl = null;
		}
	}

	afterUpdate(() => {
		//hack to prevent image flickering on scroll, we can enable parallax for slot by applying transformY directly on children class
		const current = document.getElementById('parallax-image');
		if (current && !forDesignPage) {
			current.style.backgroundPositionY = `${y / 3}px`;
		}
	});
</script>

<div class="px-hub-parallax-header-wrapper">
	<div
		class="px-cover-bg-image"
		id="parallax-image"
		role="img"
		aria-label="Sign in page background image"
		style={`${
			backgroundImgUrl ? `background-image:url(${backgroundImgUrl})` : ''
		};background-color: ${$hub.theme.coverBackgroundColor}`}
	>
		<div class="children" style={`background-color: ${$hub.theme.coverTintColorWithOpacity}`}>
			{#if forDesignPage && device == 'mobile'}
				<slot />
			{:else}
				<BreakPoint>
					<slot />
				</BreakPoint>
			{/if}
		</div>
	</div>
</div>

<svelte:window bind:scrollY={y} />

<style>
	.px-hub-parallax-header-wrapper {
		z-index: var(--z-index-Parallax);
		block-size: var(--COVER_HEIGHT);
		inline-size: 100%;
	}

	#parallax-image {
		position: relative;
		block-size: var(--COVER_HEIGHT);
		background-repeat: no-repeat;
		background-size: cover;
	}

	div {
		block-size: inherit;
		inline-size: inherit;
	}

	.children {
		position: relative;
		z-index: var(--z-index-Parallax);
		block-size: 100%;
		inline-size: 100%;
		background-repeat: no-repeat;
		background-position: 50% 50%;
		background-size: cover;
	}
</style>
